import { useEffect, useState, type FC } from 'react';
import { Button, Flex } from 'antd';
import { VanillaCalendarBase } from './ui/vanilla-calendar-base/vanilla-calendar-base';
import styles from './period.module.scss';
import dayjs from 'dayjs';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Period as PeriodEnum, type DateFilterValue } from './lib/types';
import { DateService } from '@shared/services/date';
import type { FilterOption } from '@shared/lib/types';

interface PeriodShortProps {
  value?: FilterOption<DateFilterValue>[] | null;
  onChange: (
    val: {
      label: string;
      value: { period: PeriodEnum; startDate: string; endDate: string };
    }[],
  ) => void;
  onCancel?: () => void;
}

export const PeriodShort: FC<PeriodShortProps> = ({ value, onChange, onCancel }) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'common.calendar',
  });

  const initialValue = value?.[0]?.value;

  const [selectedPeriod, setSelectedPeriod] = useState<PeriodEnum | null>(
    (initialValue?.period as PeriodEnum) ?? null,
  );

  const [selectedDates, setSelectedDates] = useState<string[]>(
    initialValue?.startDate && initialValue?.endDate
      ? [initialValue.startDate, initialValue.endDate]
      : [],
  );

  useEffect(() => {
    const nextValue = value?.[0]?.value;
    if (!nextValue) {
      setSelectedPeriod(null);
      setSelectedDates([]);
      return;
    }

    setSelectedPeriod((nextValue.period as PeriodEnum) ?? null);

    if (nextValue.startDate && nextValue.endDate) {
      setSelectedDates([nextValue.startDate, nextValue.endDate]);
      return;
    }

    if (nextValue.startDate) {
      setSelectedDates([nextValue.startDate]);
      return;
    }

    setSelectedDates([]);
  }, [value]);

  const handleCalendarChange = (dates: string[]) => {
    setSelectedPeriod(PeriodEnum.CUSTOM);
    setSelectedDates(dates);
  };

  const handleSubmit = () => {
    if (!selectedPeriod || selectedDates.length === 0) return;

    const [rawStart, rawEnd] =
      selectedDates.length === 1 ? [selectedDates[0], selectedDates[0]] : selectedDates;

    const startDate =
      selectedPeriod === PeriodEnum.CUSTOM
        ? dayjs(rawStart).startOf('day').format('YYYY-MM-DDTHH:mm:ss')
        : rawStart;

    const endDate =
      selectedPeriod === PeriodEnum.CUSTOM
        ? dayjs(rawEnd).endOf('day').format('YYYY-MM-DDTHH:mm:ss')
        : rawEnd;

    const label =
      selectedPeriod === PeriodEnum.CUSTOM
        ? DateService.formatRange(startDate, endDate, 'DD.MM.YYYY')
        : t(selectedPeriod);

    onChange([
      {
        label,
        value: {
          period: selectedPeriod,
          startDate,
          endDate,
        },
      },
    ]);
  };

  const isSubmitDisabled =
    !selectedPeriod || selectedPeriod === PeriodEnum.CUSTOM
      ? selectedDates.length === 0
      : selectedDates.length !== 2;

  return (
    <Flex className={styles.root} justify="center" gap={16}>
      <Flex className={styles.calendar} vertical gap={16}>
        <VanillaCalendarBase
          selectedDates={selectedDates}
          selectionDatesMode="multiple-ranged"
          onSetDate={handleCalendarChange}
        />

        <Flex gap="12px" align="center" className={styles.button_container}>
          <Button
            variant="text"
            className={classNames(styles.button, styles.button_cancel)}
            onClick={onCancel}
          >
            {t('cancel')}
          </Button>

          <Button
            className={styles.button}
            type="primary"
            htmlType="submit"
            disabled={isSubmitDisabled}
            onClick={handleSubmit}
          >
            {t('submit')}
          </Button>
        </Flex>
      </Flex>
    </Flex>
  );
};
